import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <Layout>
        <div className="pt-24 pb-20 px-6 text-center">
          <h1 className="text-2xl font-heading text-foreground-500">文章未找到</h1>
          <Link to="/articles" className="mt-4 inline-flex items-center gap-2 text-sm text-primary-500">
            <i className="ri-arrow-left-line"></i> 返回灵感笔记
          </Link>
        </div>
      </Layout>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 2);

  const renderContent = (markdown: string) => {
    const lines = markdown.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-xl md:text-2xl font-heading text-foreground-900 mt-10 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
        i++;
        continue;
      }

      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-lg md:text-xl font-heading text-foreground-800 mt-8 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
        i++;
        continue;
      }

      if (line.startsWith('|')) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          tableLines.push(lines[i].trim());
          i++;
        }
        elements.push(
          <div key={i} className="mt-4 mb-6 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <tbody>
                {tableLines
                  .filter((l) => !l.match(/^\|[-| :]+\|$/))
                  .map((row, ri) => {
                    const cells = row.split('|').filter((c) => c.trim());
                    const isHeader = ri === 0;
                    const CellTag = isHeader ? 'th' : 'td';
                    return (
                      <tr
                        key={ri}
                        className={isHeader ? 'bg-secondary-50' : 'border-t border-background-200'}
                      >
                        {cells.map((cell, ci) => (
                          <CellTag
                            key={ci}
                            className={`px-4 py-2.5 ${isHeader ? 'font-medium text-foreground-700' : 'text-foreground-600'}`}
                          >
                            {cell.replace(/\*\*/g, '')}
                          </CellTag>
                        ))}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        );
        continue;
      }

      if (line.startsWith('- ') || line.match(/^\d+\. /)) {
        const listItems: string[] = [];
        while (i < lines.length) {
          const li = lines[i].trim();
          if (li.startsWith('- ') || li.match(/^\d+\. /)) {
            listItems.push(li.replace(/^[\d]+\.\s*/, '').replace(/^-\s*/, ''));
            i++;
          } else {
            break;
          }
        }
        elements.push(
          <ul key={i} className="mt-2 mb-4 space-y-1.5 list-disc list-inside text-sm text-foreground-600 leading-relaxed">
            {listItems.map((item, li) => (
              <li key={li} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </ul>
        );
        continue;
      }

      if (line === '') {
        i++;
        continue;
      }

      elements.push(
        <p key={i} className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
          {line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}
        </p>
      );
      i++;
    }

    return elements;
  };

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.date,
            url: `https://ruoruworld.github.io/article/${article.slug}`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ruoruworld.github.io/' },
              { '@type': 'ListItem', position: 2, name: '灵感笔记', item: 'https://ruoruworld.github.io/articles' },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          }),
        }}
      />

      <article className="pt-24 pb-20 md:pt-28 md:pb-28 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '灵感笔记', path: '/articles' },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mt-8">
            <div className="flex items-center gap-3 text-xs text-foreground-400">
              <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                {article.category}
              </span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
              <span>·</span>
              <span>{article.author}</span>
            </div>
            <h1 className="mt-4 text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 tracking-wide leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-base text-foreground-500 leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          {/* Cover Image */}
          <div className="mt-8 w-full h-64 md:h-80 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div className="mt-10">
            {renderContent(article.content)}
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-10 border-t border-background-200">
              <h3 className="text-lg font-heading text-foreground-800">相关文章</h3>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/40 transition-all duration-300"
                  >
                    <div className="w-full h-40 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-foreground-400">{rel.category}</span>
                      <h4 className="mt-1 text-sm font-heading text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {rel.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Back Link */}
          <div className="mt-12">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors"
            >
              <i className="ri-arrow-left-line"></i> 返回灵感笔记
            </Link>
          </div>
        </div>
      </article>
    </Layout>
  );
}